import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ObjectSchema } from 'yup';
import { captureFormValidationError } from '../../../util/SentryUtils';
import { ConfigStatus } from '../../AdminPanel/DataDisplay/useDisplayConfigState';
import { getDefaultILevelConfig, ILevelFormValues, iLevelConfigSchema } from './iLevelConfig.schema';
import { useILevelConfigActions } from './useILevelConfigActions';
import { trackEvent } from '../../../util/datadogTracking';
import { AppName } from '../../../data-models/integrations-app.data-model';

export function useILevelConfigFormState() {
  const [editStatus, setEditStatus] = useState<ConfigStatus>('initial');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const { config, isConnected, isLoading, saveConfig, resetConfig } = useILevelConfigActions();

  const schema = iLevelConfigSchema(isConnected);
  const methods = useForm<ILevelFormValues>({
    resolver: yupResolver(schema as ObjectSchema<ILevelFormValues>),
    defaultValues: getDefaultILevelConfig(),
    mode: 'all',
  });

  const {
    getValues,
    formState: { errors, isDirty },
    reset,
    trigger,
  } = methods;

  // Populate form when config loads from server
  useEffect(() => {
    if (config?.config) {
      reset({
        clientId: config.config.clientId || '',
        clientSecret: '',
      });
    }
  }, [config, reset]);

  useEffect(() => {
    if (isDirty && editStatus === 'initial') {
      setEditStatus('modified');
    }
  }, [isDirty, editStatus]);

  const onSave = useCallback(async () => {
    const isValid = await trigger();
    if (!isValid) {
      captureFormValidationError('iLevel Config', errors);
      return;
    }
    try {
      const result = await saveConfig(getValues());
      setSuccessMessage(result.message);
      setErrorMessage('');
      setEditStatus('saved');
      reset(getValues());
      trackEvent('integration.connected', { provider: AppName.ILevel, success: true });
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      setErrorMessage(axiosError?.response?.data?.message || 'Failed to configure iLevel connection');
      setSuccessMessage('');
      trackEvent('integration.connected', { provider: AppName.ILevel, success: false });
    }
  }, [trigger, saveConfig, getValues, errors, reset]);

  const onReset = useCallback(async () => {
    await resetConfig();
    setEditStatus('initial');
    setSuccessMessage('');
    setErrorMessage('');
  }, [resetConfig]);

  return {
    editStatus,
    isConnected,
    isLoading,
    successMessage,
    errorMessage,
    onSave,
    onReset,
    methods,
  };
}
